import { useEffect, useRef, useState } from 'react';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import {
  isPipedreamApp,
  readCatalogue,
  uniqueApps,
  type CatalogueEntry,
  type CatalogueResponse,
  type PipedreamApp,
} from './pipedream-catalogue-api';
import { workspaceErrorCopy } from './workspace-errors';

export function usePipedreamCatalogue(
  query: string,
  suggested: boolean,
  preferredApps: string[],
  /** A change re-reads the current first page in place, e.g. after a connection was added. */
  refreshKey = 0,
  /** Leave out customer-owned apps (and their count) where their setup form is not hosted. */
  hideCustomerApps = false,
) {
  const locale = useChatInterfaceLocale();
  const loadError = workspaceErrorCopy(locale).integrationsUnavailable;
  // The list and its counts as this surface shows them.
  const shown = (catalogue: CatalogueResponse): CatalogueResponse => {
    if (!hideCustomerApps) return catalogue;
    const apps = catalogue.apps.filter((entry) => entry.provider !== 'customer_app');
    const hidden = catalogue.apps.length - apps.length;
    return {
      ...catalogue,
      apps,
      totalCount:
        catalogue.totalCount == null ? catalogue.totalCount : catalogue.totalCount - hidden,
    };
  };
  const [result, setResult] = useState<CatalogueResponse | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pageError, setPageError] = useState(false);
  const [retry, setRetry] = useState(0);
  const generation = useRef(0);
  const preferred = preferredApps.slice(0, 8).join(',');

  useEffect(() => {
    const version = ++generation.current;
    setLoading(true);
    setLoadingMore(false);
    setError(null);
    setPageError(false);
    setResult(null);
    const timer = window.setTimeout(() => {
      void Promise.all([
        readCatalogue(query.trim()),
        suggested && preferred ? readCatalogue('', undefined, preferred) : Promise.resolve(null),
      ])
        .then(([response, recommendations]) => {
          if (version !== generation.current) return;
          const catalogue = shown(response);
          if (!query.trim()) setTotal(catalogue.totalCount ?? null);
          if (catalogue.unavailable) setError(loadError);
          setResult({
            ...catalogue,
            apps: uniqueApps([...(recommendations?.apps ?? []), ...catalogue.apps]),
          });
        })
        .catch(() => {
          if (version === generation.current) setError(loadError);
        })
        .finally(() => {
          if (version === generation.current) setLoading(false);
        });
    }, 200);
    return () => {
      generation.current += 1;
      window.clearTimeout(timer);
    };
  }, [query, suggested, preferred, retry, loadError]);

  // Same search, without the loading state: the cards on screen take their fresh state.
  const refreshed = useRef(refreshKey);
  useEffect(() => {
    if (refreshed.current === refreshKey) return;
    refreshed.current = refreshKey;
    const version = generation.current;
    void readCatalogue(query.trim())
      .then((response) => {
        if (version !== generation.current) return;
        const catalogue = shown(response);
        if (!query.trim()) setTotal(catalogue.totalCount ?? null);
        const key = (app: CatalogueEntry) => `${app.provider ?? 'pipedream'}:${app.slug}`;
        const fresh = new Map(catalogue.apps.map((app) => [key(app), app]));
        setResult((current) =>
          current
            ? { ...current, apps: current.apps.map((app) => fresh.get(key(app)) ?? app) }
            : catalogue,
        );
      })
      .catch(() => undefined);
  }, [refreshKey, query]);

  async function loadMore() {
    if (!result?.nextCursor || loadingMore) return;
    const version = generation.current;
    const cursor = result.nextCursor;
    setLoadingMore(true);
    setPageError(false);
    try {
      const next = await readCatalogue(query.trim(), cursor);
      if (version !== generation.current) return;
      setResult((current) => ({
        ...next,
        // The first page counted Poneva-run entries too; later pages carry provider apps only.
        totalCount: current?.totalCount ?? next.totalCount,
        apps: uniqueApps([...(current?.apps ?? []), ...next.apps]),
        nextCursor: next.nextCursor === cursor ? null : next.nextCursor,
      }));
    } catch {
      if (version === generation.current) setPageError(true);
    } finally {
      if (version === generation.current) setLoadingMore(false);
    }
  }

  function updateApp(slug: string, patch: Partial<PipedreamApp>) {
    setResult(
      (current) =>
        current && {
          ...current,
          apps: current.apps.map((app) =>
            isPipedreamApp(app) && app.slug === slug ? { ...app, ...patch } : app,
          ),
        },
    );
  }

  return {
    result,
    total,
    loading,
    loadingMore,
    error,
    pageError,
    loadMore,
    updateApp,
    retry: () => setRetry((value) => value + 1),
  };
}
